import { TooltipProvider } from "@/components/ui/tooltip";
import PropertySidebar from "@/components/v2/molecules/admin/tabs/AdminCalendarTab/PropertySidebar";
import CalendarDayOptionsModal from "@/components/v2/organisms/admin/tabs/AdminCalendarTab/CalendarDayOptionsModal";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import { useBlockedDates } from "@/hooks/calendar/useBlockedDates";
import { useBookingState } from "@/hooks/calendar/useBookingState";
import { useCalendarDays } from "@/hooks/calendar/useCalendarDays";
import { useCalendarData } from "@/hooks/calendar/useCalendarData";
import useCalendarPermissions from "@/hooks/calendar/useCalendarPermissions";
import { useCalendarScroll } from "@/hooks/calendar/useCalendarScroll";
import { useCalendarSelection } from "@/hooks/calendar/useCalendarSelection";
import { useMonthSyncEffect } from "@/hooks/calendar/useMonthSyncEffect";
import { type Property } from "@/types/property";
import { getMonthsByDayIndex } from "@/utils/getMonthsByDayIndex";
import { useEffect, useMemo, useRef } from "react";
import BookingFormModal from "../../../../../BookingFormModal";
import CalendarVirtualized from "./CalendarVirtualized";
import ReservationDetailModal from "./ReservationDetailModal";
import { useUpdateBookingStatus } from "@/hooks/useBookings";
import { useOrigins } from "@/hooks/useOrigins";
import { startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";

interface MultiPropertyCalendarProps {
  properties: Property[];
  visibleMonthIndex: number;
  setVisibleMonthIndex: (index: number) => void;
  setVisibleMonth: (month: string) => void;
  hasScrolledToTodayRef: React.MutableRefObject<boolean>;
  onScrollFunctionsReady?: (fns: {
    scrollToPreviousWeek: () => void;
    scrollToNextWeek: () => void;
    scrollToToday: () => void;
    scrollToDay: (date: Date) => void;
  }) => void;
}

const MultiPropertyCalendar = ({ 
  properties,
  visibleMonthIndex,
  setVisibleMonthIndex,
  setVisibleMonth,
  hasScrolledToTodayRef,
  onScrollFunctionsReady,
}: MultiPropertyCalendarProps) => {
  const cellWidth = 60;
  const rowHeight = 60;
  const labelColumnWidth = 260;

  const today = new Date();

  const calendarScrollRef = useRef<HTMLDivElement>(null);
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const monthHeaderRef = useRef<HTMLDivElement>(null);

  const {
    canEditReservation,
    canCancelReservation,
    canClickBlockedDates,
    canClickBookedDates,
    canClickFutureDates,
    canClickPastDates,
    role,
  } = useRolePermissions();

  const { handleBlockDate, refetchAllAvailability } =
    useBlockedDates(properties);


  const days = useCalendarDays();
  const months = useMemo(() => getMonthsByDayIndex(days), [days]);

  // Calculate visible date range for optimized data fetching
  const visibleDateRange = useMemo(() => {
    const now = new Date();
    const start = startOfMonth(subMonths(now, 2));
    const end = endOfMonth(addMonths(now, 4));
    return { start, end };
  }, []);

  // Use optimized calendar data hook
  const propertyIds = useMemo(
    () => properties.map((p) => p.id),
    [properties]
  );

  const {
    getDayStatus: getOptimizedDayStatus,
    bookingsByProperty,
    blockedDatesByProperty,
    refetch: refetchCalendarData,
  } = useCalendarData(propertyIds, visibleDateRange.start, visibleDateRange.end);

  // Helper function to check if a date is manually blocked (for modal)
  const isDateManuallyBlocked = (date: Date, propertyId: string): boolean => {
    const dateStr = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      .toISOString()
      .split("T")[0];
    const blockedDates = blockedDatesByProperty.get(propertyId);
    return blockedDates ? blockedDates.has(dateStr) : false;
  };

  const {
    selectedBooking,
    showBookingModal,
    showBookingFormModal,
    selectedDateForReservation,
    selectedPropertyForReservation,
    setShowBookingFormModal,
    setShowBookingModal,
    handleAddReservation,
    handleBookingClick,
    handleBookingCreated,
    handleCancelBooking: handleCancelBookingModal,
  } = useBookingState();

  const updateStatusMutation = useUpdateBookingStatus();
  const { data: origins = [] } = useOrigins();

  const handleCancelBooking = (bookingId: string) => {
    updateStatusMutation.mutate({ 
      id: bookingId, 
      status: "Cancelada" 
    });
  };

  const {
    getFirstVisibleDayIndex,
    scrollToDay,
    scrollToPreviousWeek,
    scrollToNextWeek,
    scrollToToday,
  } = useCalendarScroll({
    cellWidth,
    days,
    monthHeaderRef,
    months,
    scrollableAreaRef: calendarScrollRef,
    today,
  });

  useMonthSyncEffect({
    scrollableAreaRef: calendarScrollRef,
    months,
    visibleMonthIndex,
    setVisibleMonthIndex,
    getFirstVisibleDayIndex,
  });

  // Update visible month name when month index changes
  useEffect(() => {
    if (months[visibleMonthIndex]) {
      setVisibleMonth(months[visibleMonthIndex].name);
    }
  }, [visibleMonthIndex, months, setVisibleMonth]);

  const { selectedDate, selectedProperty, showModal, openModal, closeModal } =
    useCalendarSelection();

  // Create compatible getDayStatus function
  const isDayBooked = (date: Date, propertyId: string): boolean => {
    return getOptimizedDayStatus(date, propertyId).isBooked;
  };

  const getDayStatus = useCalendarPermissions({
    today,
    canClickBlockedDates,
    canClickBookedDates,
    canClickFutureDates,
    canClickPastDates,
    isDayBooked: (date, propertyId) => getOptimizedDayStatus(date, propertyId).isBooked,
    isDateManuallyBlocked: (date, propertyId) => {
      const dateStr = new Date(date.getFullYear(), date.getMonth(), date.getDate())
        .toISOString()
        .split("T")[0];
      const blockedDates = blockedDatesByProperty.get(propertyId);
      return blockedDates ? blockedDates.has(dateStr) : false;
    },
  });

  useEffect(() => {
    if (hasScrolledToTodayRef.current) {
      scrollToToday("instant");
    } else {
      scrollToDay(days[0], "instant", 0);
      hasScrolledToTodayRef.current = true;
    }
  }, [days]);

  // Expose scroll functions to parent component
  useEffect(() => {
    if (onScrollFunctionsReady) {
      onScrollFunctionsReady({
        scrollToPreviousWeek,
        scrollToNextWeek,
        scrollToToday: () => scrollToToday("smooth"),
        scrollToDay: (date: Date) => scrollToDay(date, "smooth", 0, true),
      });
    }
  }, [scrollToPreviousWeek, scrollToNextWeek, scrollToToday, scrollToDay, onScrollFunctionsReady]);

  return (
    <TooltipProvider>
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden w-full">
        <div className="flex">
          <PropertySidebar
            filteredProperties={properties}
            labelColumnWidth={labelColumnWidth}
            properties={properties}
            rowHeight={rowHeight}
            sidebarScrollRef={sidebarScrollRef}
            hasScrolledToTodayRef={hasScrolledToTodayRef}
            role={role}
          />

        <div className="flex flex-col flex-1 overflow-hidden relative">
          {/* Espacio reservado - navegación movida a CalendarFiltersBar */}
          <div className="absolute top-3 right-4 z-30 flex items-center gap-2">
          </div>

          <CalendarVirtualized
            calendarScrollRef={calendarScrollRef}
            properties={properties}
            days={days}
            months={months}
            cellWidth={cellWidth}
            rowHeight={rowHeight}
            today={today}
            bookingsByProperty={bookingsByProperty}
            onBookingClick={handleBookingClick}
            onOpenModal={openModal}
            getDayStatus={getDayStatus}
          />
        </div>
      </div>
      </div>

      <ReservationDetailModal
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            document.body.style.pointerEvents = '';
          }}
          booking={selectedBooking}
          onCancelBooking={handleCancelBooking}
          onEdit={() => {
            setShowBookingModal(false);
            setShowBookingFormModal(true);
          }}
          canEditReservation={canEditReservation}
          canCancelReservation={canCancelReservation}
        />

        <BookingFormModal
          isOpen={showBookingFormModal}
          onClose={() => setShowBookingFormModal(false)}
          booking={selectedBooking}
          preselectedPropertyId={selectedPropertyForReservation}
          preselectedCheckInDate={selectedDateForReservation}
          onBookingCreated={handleBookingCreated}
          origins={origins}
        />

        {selectedDate && selectedProperty && (
          <CalendarDayOptionsModal
            isOpen={showModal}
            onClose={closeModal}
            selectedDate={selectedDate}
            selectedProperty={selectedProperty}
            isDateBlocked={isDateManuallyBlocked(
              selectedDate,
              selectedProperty.id,
            )}
            onAddReservation={() => {
              if (selectedDate && selectedProperty && handleAddReservation) {
                handleAddReservation(selectedDate, selectedProperty);
                closeModal();
              }
            }}
            onBlockDate={() => {
              if (selectedDate && selectedProperty && handleBlockDate) {
                handleBlockDate(selectedDate, selectedProperty, () => {
                  refetchAllAvailability();
                  refetchCalendarData();
                });
                closeModal();
              }
            }}
          />
        )}
    </TooltipProvider>
  );
};

export default MultiPropertyCalendar;
